import { PopoverOrigin } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { SubmenuSide } from '../menu.model';

type UseSubmenuSideArgs = {
  /** Whether the popover is a submenu. Origins are only resolved for submenus. */
  type: 'root' | 'submenu';
  open: boolean;
  anchorEl?: Element | null;
  /** Rendered popover paper, used to measure the submenu width. */
  paperElement: HTMLElement | null;
  /** Side the submenu should open towards, inherited from its parent menu. */
  preferredSide: SubmenuSide;
  /** Anchor width, used to recompute the flip when the anchor resizes. */
  anchorWidth: number | undefined;
};

const ORIGINS_BY_SIDE: Record<
  SubmenuSide,
  { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin }
> = {
  right: {
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
  left: {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
};

/**
 * Resolves which side a submenu opens towards, flipping to the opposite side
 * when the {@link UseSubmenuSideArgs.preferredSide} would overflow the viewport.
 * When neither side fits, the side with more room is chosen and the popover is
 * left to clamp into the viewport.
 */
export const useSubmenuSide = ({
  type,
  open,
  anchorEl,
  paperElement,
  preferredSide,
  anchorWidth,
}: UseSubmenuSideArgs) => {
  const [resolvedSide, setResolvedSide] = useState<SubmenuSide>(preferredSide);

  // Reset to the inherited preference whenever it changes or the menu reopens,
  // so the flip decision is recomputed against fresh layout.
  useEffect(() => {
    if (open) setResolvedSide(preferredSide);
  }, [open, preferredSide]);

  useLayoutEffect(() => {
    if (type !== 'submenu' || !open || !anchorEl || !paperElement) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const paperWidth = paperElement.getBoundingClientRect().width;
    const spaceRight = window.innerWidth - anchorRect.right;
    const spaceLeft = anchorRect.left;

    setResolvedSide((current) => {
      const fits = (side: SubmenuSide) =>
        side === 'right' ? spaceRight >= paperWidth : spaceLeft >= paperWidth;

      if (fits(preferredSide)) return preferredSide;
      const opposite: SubmenuSide =
        preferredSide === 'right' ? 'left' : 'right';
      if (fits(opposite)) return opposite;
      // Neither side fits fully: pick the side with more room.
      const next: SubmenuSide = spaceRight >= spaceLeft ? 'right' : 'left';
      return next === current ? current : next;
    });
  }, [type, open, anchorEl, paperElement, preferredSide, anchorWidth]);

  const origins = useMemo(() => ORIGINS_BY_SIDE[resolvedSide], [resolvedSide]);

  return { resolvedSide, ...origins };
};
